import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Archive, Ban, Download, Plus, Printer, Search, Trash2 } from 'lucide-react';
import { PrintInventoryLabelsModal } from '@/components/inventory/PrintInventoryLabelsModal.js';
import {
  createInventoryItem,
  INVENTORY_CACHE_PREFIXES,
  listInventoryItemIds,
  listInventoryItems,
  downloadInventoryExport,
  unarchiveInventoryItem,
  type InventoryItemListRow,
} from '@/lib/api/inventory.js';
import { listInventoryCategories, type InventoryCategoryNode } from '@/lib/api/inventory-categories.js';
import { listDeviceMakes, listDeviceModels } from '@/lib/api/devices.js';
import { EmptyState } from '@/components/common/EmptyState.js';
import { TableRowSkeleton } from '@/components/common/LoadingSkeleton.js';
import { Pagination } from '@/components/common/Pagination.js';
import { SlideOver } from '@/components/SlideOver.js';
import { StockAdjustModal } from '@/components/inventory/StockAdjustModal.js';
import {
  ArchiveInventoryModal,
  BulkArchiveInventoryModal,
  BulkDeleteInventoryModal,
  BulkDiscontinueInventoryModal,
  DeleteInventoryModal,
} from '@/components/inventory/InventoryArchiveModals.js';
import { formatCurrency, formatDate, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { usePermissions } from '@/hooks/usePermissions.js';
import { isManagerRole } from '@/lib/permissions.js';
import { useAuthStore } from '@/stores/auth.js';
import { cn } from '@/lib/utils.js';

const PAGE_SIZE = 50;

const categories = [
  { value: '', label: 'All types' },
  { value: 'repair_part', label: 'Repair part' },
  { value: 'retail_accessory', label: 'Retail accessory' },
  { value: 'trade_in', label: 'Trade-in' },
  { value: 'refurbished', label: 'Refurbished' },
];

function flattenCategories(
  nodes: InventoryCategoryNode[],
  depth = 0,
): Array<{ id: string; label: string }> {
  const out: Array<{ id: string; label: string }> = [];
  for (const n of nodes) {
    out.push({ id: n.id, label: `${'— '.repeat(depth)}${n.name}` });
    out.push(...flattenCategories(n.children ?? [], depth + 1));
  }
  return out;
}

function AddItemForm({
  loading,
  onSubmit,
}: {
  loading?: boolean;
  onSubmit: (values: Record<string, unknown>) => void;
}) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('repair_part');
  const [retailPrice, setRetailPrice] = useState('0');
  const [serialized, setSerialized] = useState(false);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit({
      name,
      category,
      retailPrice: Number(retailPrice),
      serialized,
      taxable: true,
      trackStock: true,
    });
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <label className="block">
        <span className="text-sm font-medium text-slate-700">Item name</span>
        <input
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium text-slate-700">Category</span>
        <select
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          {categories.slice(1).map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="text-sm font-medium text-slate-700">Retail price</span>
        <input
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
          type="number"
          min="0"
          step="0.01"
          value={retailPrice}
          onChange={(e) => setRetailPrice(e.target.value)}
        />
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={serialized}
          onChange={(e) => setSerialized(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
        />
        Serialized item
      </label>
      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#164477] disabled:opacity-60"
      >
        {loading ? 'Creating...' : 'Add item'}
      </button>
    </form>
  );
}

function invalidateInventoryCaches(queryClient: ReturnType<typeof useQueryClient>) {
  for (const key of INVENTORY_CACHE_PREFIXES) {
    void queryClient.invalidateQueries({ queryKey: [key] });
  }
}

export function InventoryListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.user?.role ?? 'read_only');
  const { can } = usePermissions();
  const canViewArchived = can('view_archived_inventory');
  const canArchive = isManagerRole(role);
  const canBulkImport = role === 'tenant_admin' || role === 'platform_admin';
  const canHardDelete = role === 'tenant_admin' || role === 'platform_admin';

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [condition, setCondition] = useState('');
  const [compatibleMakeId, setCompatibleMakeId] = useState('');
  const [compatibleModelId, setCompatibleModelId] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [itemKind, setItemKind] = useState<'all' | 'bundles' | 'variants'>('all');
  const [status, setStatus] = useState<'active' | 'archived' | 'discontinued' | 'all'>('active');
  const [sort, setSort] = useState<'name' | 'qty' | 'lastReceived' | 'category'>('name');
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [adjustItem, setAdjustItem] = useState<{ id: string; name: string; qty: number } | null>(
    null,
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [archiveTarget, setArchiveTarget] = useState<InventoryItemListRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InventoryItemListRow | null>(null);
  const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);
  const [bulkDiscontinueOpen, setBulkDiscontinueOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkPrintOpen, setBulkPrintOpen] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    category,
    categoryId,
    condition,
    compatibleMakeId,
    compatibleModelId,
    lowStockOnly,
    itemKind,
    status,
    sort,
  ]);

  const listParams = useMemo(() => {
    const params: Record<string, unknown> = { page, pageSize: PAGE_SIZE, sort };
    if (debouncedSearch) params.search = debouncedSearch;
    if (category) params.category = category;
    if (categoryId) params.categoryId = categoryId;
    if (condition) params.condition = condition;
    if (compatibleModelId) params.compatibleModelId = compatibleModelId;
    else if (compatibleMakeId) params.compatibleMakeId = compatibleMakeId;
    if (lowStockOnly) params.lowStockOnly = true;
    if (itemKind === 'bundles') params.isBundle = true;
    if (itemKind === 'variants') params.hasVariants = true;
    if (status === 'all' && canViewArchived) params.status = 'all';
    else if (status === 'archived' && canViewArchived) params.status = 'archived';
    else if (status === 'discontinued') params.status = 'discontinued';
    else params.status = 'active';
    return params;
  }, [
    page,
    sort,
    debouncedSearch,
    category,
    categoryId,
    condition,
    compatibleMakeId,
    compatibleModelId,
    lowStockOnly,
    itemKind,
    status,
    canViewArchived,
  ]);

  const categoriesTreeQuery = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: listInventoryCategories,
  });
  const makesQuery = useQuery({ queryKey: ['device-makes'], queryFn: listDeviceMakes });
  const modelsQuery = useQuery({
    queryKey: ['device-models', compatibleMakeId],
    queryFn: () => listDeviceModels({ makeId: compatibleMakeId }),
    enabled: Boolean(compatibleMakeId),
  });
  const categoryOptions = useMemo(
    () => flattenCategories(categoriesTreeQuery.data ?? []),
    [categoriesTreeQuery.data],
  );

  const itemsQuery = useQuery({
    queryKey: ['inventory-items', listParams],
    queryFn: () => listInventoryItems(listParams),
  });

  const meta = itemsQuery.data?.meta;
  const total = meta?.total ?? 0;
  const rows = itemsQuery.data?.data ?? [];
  const pageIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const createMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => createInventoryItem(values),
    onSuccess: () => {
      invalidateInventoryCaches(queryClient);
      setAddOpen(false);
      toastSuccess('Inventory item created successfully');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const unarchiveMut = useMutation({
    mutationFn: (id: string) => unarchiveInventoryItem(id),
    onSuccess: () => {
      invalidateInventoryCaches(queryClient);
      toastSuccess('Item restored');
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectPage() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        for (const id of pageIds) next.delete(id);
      } else {
        for (const id of pageIds) next.add(id);
      }
      return next;
    });
  }

  async function selectAllMatching() {
    try {
      const filterParams = { ...listParams };
      delete filterParams.page;
      delete filterParams.pageSize;
      const res = await listInventoryItemIds(filterParams);
      setSelected(new Set(res.data));
      toastSuccess(`Selected ${res.data.length} items`);
    } catch (error) {
      toastError(parseApiError(error).message);
    }
  }

  async function downloadExport(format: 'xlsx' | 'csv') {
    try {
      const blob = await downloadInventoryExport(listParams, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory-${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toastError(parseApiError(error).message);
    }
  }

  function invalidate() {
    invalidateInventoryCaches(queryClient);
    setSelected(new Set());
  }

  const selectedIds = [...selected];

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:flex-wrap">
          <div className="relative min-w-[200px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-3 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              placeholder="Search name, SKU, or barcode"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
          >
            <option value="active">Active</option>
            <option value="discontinued">Discontinued</option>
            {canViewArchived ? <option value="archived">Archived</option> : null}
            {canViewArchived ? <option value="all">All</option> : null}
          </select>
          <select
            className="rounded-xl border border-slate-300 bg-white px-3 py-2.5"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {categories.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <select
            className="rounded-xl border border-slate-300 bg-white px-3 py-2.5"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">All categories</option>
            {categoryOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
          <select
            className="rounded-xl border border-slate-300 bg-white px-3 py-2.5"
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
          >
            <option value="">All conditions</option>
            <option value="new">New</option>
            <option value="used">Used</option>
            <option value="refurbished">Refurbished</option>
            <option value="oem">OEM</option>
            <option value="aftermarket">Aftermarket</option>
          </select>
          <select
            className="rounded-xl border border-slate-300 bg-white px-3 py-2.5"
            value={compatibleMakeId}
            onChange={(e) => {
              setCompatibleMakeId(e.target.value);
              setCompatibleModelId('');
            }}
          >
            <option value="">Compatible make</option>
            {(makesQuery.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <select
            className="rounded-xl border border-slate-300 bg-white px-3 py-2.5"
            value={compatibleModelId}
            onChange={(e) => setCompatibleModelId(e.target.value)}
            disabled={!compatibleMakeId}
          >
            <option value="">Compatible model</option>
            {(modelsQuery.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <select
            className="rounded-xl border border-slate-300 bg-white px-3 py-2.5"
            value={sort}
            onChange={(e) => setSort(e.target.value as typeof sort)}
          >
            <option value="name">Sort: Name</option>
            <option value="qty">Sort: Qty</option>
            <option value="lastReceived">Sort: Last received</option>
            <option value="category">Sort: Category</option>
          </select>
          <label className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm">
            <input
              type="checkbox"
              checked={lowStockOnly}
              onChange={(e) => setLowStockOnly(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
            />
            Low stock only
          </label>
          <select
            className="rounded-xl border border-slate-300 bg-white px-3 py-2.5"
            value={itemKind}
            onChange={(e) => setItemKind(e.target.value as typeof itemKind)}
          >
            <option value="all">All items</option>
            <option value="bundles">Bundles only</option>
            <option value="variants">Has variants</option>
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold"
          >
            <Plus className="h-4 w-4" /> Quick add
          </button>
          <button
            type="button"
            onClick={() => navigate('/inventory/new')}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white"
          >
            <Plus className="h-4 w-4" /> Full add
          </button>
          <button
            type="button"
            onClick={() => void downloadExport('xlsx')}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold"
          >
            <Download className="h-4 w-4" /> Export .xlsx
          </button>
          <button
            type="button"
            onClick={() => void downloadExport('csv')}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold"
          >
            CSV
          </button>
          {canBulkImport ? (
            <button
              type="button"
              onClick={() => navigate('/inventory/bulk')}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold"
            >
              Import edits
            </button>
          ) : null}
        </div>
      </div>

      {selected.size > 0 ? (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
          <span className="font-semibold">{selected.size} selected</span>
          <button type="button" className="underline" onClick={() => setSelected(new Set())}>
            Clear
          </button>
          {selected.size < total ? (
            <button type="button" className="text-primary underline" onClick={() => void selectAllMatching()}>
              Select all {total} matching
            </button>
          ) : null}
          <div className="ml-auto flex flex-wrap gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-semibold"
              onClick={() => setBulkPrintOpen(true)}
            >
              <Printer className="h-4 w-4" /> Print labels
            </button>
            {canArchive ? (
              <>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-semibold"
                  onClick={() => setBulkArchiveOpen(true)}
                >
                  <Archive className="h-4 w-4" /> Archive
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-semibold"
                  onClick={() => setBulkDiscontinueOpen(true)}
                >
                  <Ban className="h-4 w-4" /> Discontinue
                </button>
              </>
            ) : null}
            {canHardDelete ? (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 font-semibold text-red-700"
                onClick={() => setBulkDeleteOpen(true)}
              >
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-3">
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={toggleSelectPage}
                  className="h-4 w-4 rounded border-slate-300"
                  aria-label="Select page"
                />
              </th>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Category</th>
              <th className="px-5 py-3">Barcode</th>
              <th className="px-5 py-3">Qty</th>
              <th className="px-5 py-3">Suppliers</th>
              <th className="px-5 py-3">Last received</th>
              <th className="px-5 py-3">Bin</th>
              <th className="px-5 py-3">Retail</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((item) => (
              <tr
                key={item.id}
                className={cn(
                  'cursor-pointer',
                  item.archivedAt
                    ? 'bg-slate-50/80 text-slate-500'
                    : item.lowStock
                      ? 'bg-amber-50/70'
                      : 'hover:bg-slate-50',
                )}
              >
                <td className="px-3 py-4" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.has(item.id)}
                    onChange={() => toggleSelect(item.id)}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                </td>
                <td
                  className="px-5 py-4 font-semibold"
                  onClick={() => navigate(`/inventory/${item.id}`)}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span>{item.name}</span>
                    {item.archivedAt ? (
                      <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-700">
                        Archived
                      </span>
                    ) : null}
                    {item.discontinuedAt && !item.archivedAt ? (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-900">
                        Discontinued
                      </span>
                    ) : null}
                    {item.isBundle ? (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-800">
                        Bundle
                      </span>
                    ) : null}
                  </div>
                </td>
                <td className="px-5 py-4 capitalize text-slate-600">
                  {item.categoryPath ?? prettyLabel(item.category)}
                </td>
                <td className="px-5 py-4 font-mono text-xs text-slate-600">
                  {item.barcodeInternal ?? '-'}
                </td>
                <td className={cn('px-5 py-4 font-semibold', item.lowStock && 'text-red-700')}>
                  {item.qtyOnHand}
                </td>
                <td className="px-5 py-4 text-slate-600">{item.supplierCount ?? 0}</td>
                <td className="px-5 py-4 text-slate-600">{formatDate(item.lastReceivedAt)}</td>
                <td className="px-5 py-4 text-slate-600">{item.binLocation ?? '-'}</td>
                <td className="px-5 py-4 text-slate-600">{formatCurrency(item.retailPrice)}</td>
                <td className="px-5 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end gap-2">
                    {item.archivedAt && canArchive ? (
                      <button
                        type="button"
                        className="text-sm font-semibold text-primary"
                        onClick={() => unarchiveMut.mutate(item.id)}
                      >
                        Unarchive
                      </button>
                    ) : null}
                    {!item.archivedAt ? (
                      <button
                        type="button"
                        className="text-sm font-semibold text-slate-700"
                        onClick={() =>
                          setAdjustItem({
                            id: item.id,
                            name: item.name,
                            qty: Number(item.qtyOnHand),
                          })
                        }
                      >
                        + Adjust
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="text-sm font-semibold text-primary"
                      onClick={() => navigate(`/inventory/${item.id}`)}
                    >
                      Open
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {itemsQuery.isLoading
              ? [1, 2, 3, 4, 5].map((i) => <TableRowSkeleton key={i} columns={10} />)
              : null}
            {!itemsQuery.isLoading && itemsQuery.isError ? (
              <tr>
                <td colSpan={10} className="px-5 py-10 text-center text-red-700">
                  {parseApiError(itemsQuery.error).message || 'Failed to load inventory'}
                  <button
                    type="button"
                    className="mt-2 block w-full text-sm font-semibold text-primary underline"
                    onClick={() => void itemsQuery.refetch()}
                  >
                    Retry
                  </button>
                </td>
              </tr>
            ) : null}
            {!itemsQuery.isLoading && !itemsQuery.isError && rows.length === 0 ? (
              <tr>
                <td colSpan={10}>
                  <EmptyState
                    icon="📦"
                    title="No inventory items"
                    description="Add your first inventory item to start tracking stock."
                    action={{ label: 'Add Item', onClick: () => setAddOpen(true) }}
                  />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        pageSize={meta?.pageSize ?? PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      <SlideOver title="Quick add item" open={addOpen} onClose={() => setAddOpen(false)}>
        <AddItemForm
          loading={createMutation.isPending}
          onSubmit={(values) => createMutation.mutate(values)}
        />
      </SlideOver>

      {adjustItem ? (
        <StockAdjustModal
          open
          onClose={() => setAdjustItem(null)}
          itemId={adjustItem.id}
          itemName={adjustItem.name}
          currentQty={adjustItem.qty}
          suppliers={[]}
          onSuccess={() => setAdjustItem(null)}
        />
      ) : null}

      <ArchiveInventoryModal
        open={Boolean(archiveTarget)}
        item={archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onDone={invalidate}
      />
      <DeleteInventoryModal
        open={Boolean(deleteTarget)}
        item={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDone={invalidate}
        onArchiveInstead={() => {
          if (deleteTarget) {
            setArchiveTarget(deleteTarget);
            setDeleteTarget(null);
          }
        }}
      />
      <BulkArchiveInventoryModal
        open={bulkArchiveOpen}
        itemIds={selectedIds}
        onClose={() => setBulkArchiveOpen(false)}
        onDone={invalidate}
      />
      <BulkDiscontinueInventoryModal
        open={bulkDiscontinueOpen}
        itemIds={selectedIds}
        onClose={() => setBulkDiscontinueOpen(false)}
        onDone={invalidate}
      />
      <BulkDeleteInventoryModal
        open={bulkDeleteOpen}
        itemIds={selectedIds}
        onClose={() => setBulkDeleteOpen(false)}
        onDone={invalidate}
      />
      <PrintInventoryLabelsModal
        open={bulkPrintOpen}
        onClose={() => setBulkPrintOpen(false)}
        title="Print labels for selected items"
        items={selectedIds.map((id) => {
          const row = rows.find((r) => r.id === id);
          return { itemId: id, name: row?.name ?? id.slice(0, 8), quantity: 1 };
        })}
      />
    </div>
  );
}
